import {
  CopyAssistEmailMediumData,
  CopyAssistPushMediumData,
  CopyAssistSMSMediumData,
} from 'modules/ai/copyAssist/constants';
import { useVariantCardContext } from 'modules/experiments/contexts';
import {
  getTakeoverCampaignMessageMedium,
  getTakeoverExperimentType,
} from 'modules/experiments/ducks/selectors';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import {
  FormFieldProps,
  VariantFormFieldKey,
  VariantTextFormField,
} from 'modules/experiments/types';
import { getFieldTemplateErrors, getVariantFormFieldValue } from 'modules/experiments/utils';
import { FieldNames } from 'modules/templateEditor/common';
import { useSelector } from 'react-redux';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import { CopyAssistTextArea } from './CopyAssistTextArea';
import { CopyAssistTextAreaWithErrorContext } from './CopyAssistTextAreaWithErrorContext';
import { CopyAssistTextBox } from './CopyAssistTextBox';
import { TextArea } from './TextArea';
import { TextAreaWithErrorContext } from './TextAreaWithErrorContext';
import { TextBox } from './TextBox';
import { TextBoxWithErrorContext } from './TextBoxWithErrorContext';

export const TextFormField = ({ formField }: FormFieldProps<VariantTextFormField>) => {
  const { t } = useExperimentsTranslations();
  const { isCardInEditMode, templateErrors, variant } = useVariantCardContext();
  const type = useSelector(getTakeoverExperimentType);
  const messageMedium = useSelector(getTakeoverCampaignMessageMedium);
  const isCopyAssistEnabled = useFeatureFlag('CopyAssist', false);
  const fieldName = formField.key;
  const fieldValues = getVariantFormFieldValue({ variant, type, messageMedium });
  const fieldValue: string = fieldValues[fieldName];

  if (isCopyAssistEnabled) {
    switch (formField.key) {
      case VariantFormFieldKey.EMAIL_SUBJECT_LINE:
        return (
          <CopyAssistTextAreaWithErrorContext
            dataTestId={formField.key}
            dataKey={`${variant.alphabet}-${formField.key}`}
            contentType={CopyAssistEmailMediumData.subject}
            fieldLabel={formField.label}
            fieldName={fieldName}
            fieldValue={fieldValue}
            fieldTemplateErrors={getFieldTemplateErrors(templateErrors, FieldNames.SUBJECT)}
            isFieldEditable={isCardInEditMode}
            isFieldFocused={formField.isFocused}
            messageMedium={messageMedium}
          />
        );

      case VariantFormFieldKey.EMAIL_PRE_HEADER:
        return (
          <CopyAssistTextArea
            dataTestId={formField.key}
            dataKey={`${variant.alphabet}-${formField.key}`}
            contentType={CopyAssistEmailMediumData.preheaderText}
            fieldLabel={formField.label}
            fieldName={fieldName}
            fieldValue={fieldValue}
            isFieldEditable={isCardInEditMode}
            isFieldFocused={formField.isFocused}
            messageMedium={messageMedium}
          />
        );

      case VariantFormFieldKey.PUSH_TITLE:
        return (
          <CopyAssistTextBox
            dataTestId={formField.key}
            dataKey={`${variant.alphabet}-${formField.key}`}
            contentType={CopyAssistPushMediumData.title}
            fieldLabel={formField.label}
            fieldName={fieldName}
            fieldValue={fieldValue}
            isFieldEditable={isCardInEditMode}
            isFieldFocused={formField.isFocused}
            messageMedium={messageMedium}
          />
        );

      case VariantFormFieldKey.PUSH_MESSAGE:
        return (
          <CopyAssistTextAreaWithErrorContext
            dataTestId={formField.key}
            dataKey={`${variant.alphabet}-${formField.key}`}
            contentType={CopyAssistPushMediumData.message}
            fieldLabel={formField.label}
            fieldName={fieldName}
            fieldValue={fieldValue}
            fieldTemplateErrors={getFieldTemplateErrors(
              templateErrors,
              FieldNames.MISSING_MESSAGE_OR_SILENT_PUSH,
            )}
            isFieldEditable={isCardInEditMode}
            isFieldFocused={formField.isFocused}
            messageMedium={messageMedium}
          />
        );

      case VariantFormFieldKey.SMS_MESSAGE:
        return (
          <CopyAssistTextArea
            dataTestId={formField.key}
            dataKey={`${variant.alphabet}-${formField.key}`}
            contentType={CopyAssistSMSMediumData.message}
            fieldLabel={formField.label}
            fieldName={fieldName}
            fieldValue={fieldValue}
            isFieldEditable={isCardInEditMode}
            isFieldFocused={formField.isFocused}
            messageMedium={messageMedium}
          />
        );

      default:
      /** Move on to the next switch */
    }
  }

  switch (formField.key) {
    case VariantFormFieldKey.EMAIL_SUBJECT_LINE:
      return (
        <TextBoxWithErrorContext
          dataTestId={formField.key}
          fieldLabel={formField.label}
          fieldName={fieldName}
          fieldValue={fieldValue}
          fieldTemplateErrors={getFieldTemplateErrors(templateErrors, FieldNames.SUBJECT)}
          isFieldEditable={isCardInEditMode}
          isFieldFocused={formField.isFocused}
        />
      );

    case VariantFormFieldKey.WEB_PUSH_ICON:
      return (
        <TextBoxWithErrorContext
          dataTestId={formField.key}
          fieldLabel={formField.label}
          fieldName={fieldName}
          fieldValue={fieldValue}
          isFieldEditable={isCardInEditMode}
          isFieldFocused={formField.isFocused}
        />
      );

    case VariantFormFieldKey.WEB_PUSH_CLICK_LOCATION:
      return (
        <TextBoxWithErrorContext
          dataTestId={formField.key}
          fieldLabel={formField.label}
          fieldName={fieldName}
          fieldValue={fieldValue}
          isFieldEditable={isCardInEditMode}
          isFieldFocused={formField.isFocused}
          helperText={t('helperText.clickLocation')}
        />
      );

    case VariantFormFieldKey.SMS_IMAGE_URL:
      return (
        <TextBoxWithErrorContext
          dataTestId={formField.key}
          fieldLabel={formField.label}
          fieldName={fieldName}
          fieldValue={fieldValue}
          isFieldEditable={isCardInEditMode}
          isFieldFocused={formField.isFocused}
          helperText={t('helperText.imageUrl')}
        />
      );

    case VariantFormFieldKey.PUSH_MESSAGE:
      return (
        <TextAreaWithErrorContext
          dataTestId={formField.key}
          fieldLabel={formField.label}
          fieldName={fieldName}
          fieldValue={fieldValue}
          fieldTemplateErrors={getFieldTemplateErrors(
            templateErrors,
            FieldNames.MISSING_MESSAGE_OR_SILENT_PUSH,
          )}
          isFieldEditable={isCardInEditMode}
          isFieldFocused={formField.isFocused}
        />
      );

    case VariantFormFieldKey.SMS_MESSAGE:
      return (
        <TextArea
          dataTestId={formField.key}
          fieldLabel={formField.label}
          fieldName={fieldName}
          fieldValue={fieldValue}
          isFieldEditable={isCardInEditMode}
          isFieldFocused={formField.isFocused}
        />
      );

    case VariantFormFieldKey.EMAIL_PRE_HEADER:
    case VariantFormFieldKey.PUSH_TITLE:
    default:
      return (
        <TextBox
          dataTestId={formField.key}
          fieldLabel={formField.label}
          fieldName={fieldName}
          fieldValue={fieldValue}
          isFieldEditable={isCardInEditMode}
          isFieldFocused={formField.isFocused}
        />
      );
  }
};
